import { useState, useEffect, useCallback, useMemo } from 'react';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import {
  listOrgMembers,
  syncClinicAssignments,
  assignUserToAllClinics,
  updateUserNpi,
  client,
} from '../../services/api';
import {
  getAgencyClinics,
  type AgencyClinicResponse,
} from '../../services/clinicsApi';
import { getHttpStatus } from '../../utils/httpErrorUtils';
import type {
  OrgMemberResponse,
  ClinicAssignmentRow,
  ClinicAssignmentUpdate,
  RoleFlag,
} from './types';

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useClinicianManager() {
  const { user, hasAnyPermission, showSuccess, showError } = useStore(
    useShallow(state => ({
      user: state.user,
      hasAnyPermission: state.hasAnyPermission,
      showSuccess: state.showSuccess,
      showError: state.showError,
    }))
  );

  // ── Data state ──
  const [members, setMembers] = useState<OrgMemberResponse[]>([]);
  const [clinics, setClinics] = useState<AgencyClinicResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // ── Edit dialog state ──
  const [editingMember, setEditingMember] = useState<OrgMemberResponse | null>(
    null
  );
  const [assignments, setAssignments] = useState<ClinicAssignmentRow[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // ── Assign-all dialog state ──
  const [showAssignAllDialog, setShowAssignAllDialog] = useState(false);
  const [isAssigningAll, setIsAssigningAll] = useState(false);
  const [assignAllDefaults, setAssignAllDefaults] = useState<
    Record<RoleFlag, boolean>
  >({
    is_assignable_clinician: false,
    is_case_manager: false,
    is_approver: false,
    is_physician: false,
  });

  // ── NPI state ──
  const [savingNpiForUserId, setSavingNpiForUserId] = useState<string | null>(
    null
  );

  // ── Data loading ──

  const loadData = useCallback(async () => {
    if (!user?.agency?.id) return;
    setIsLoading(true);
    try {
      const [membersRes, clinicsData] = await Promise.all([
        listOrgMembers({ client }),
        getAgencyClinics(user.agency.id),
      ]);
      setMembers(membersRes.data ?? []);
      setClinics(clinicsData);
    } catch (err) {
      console.error('Failed to load clinician manager data:', err);
      showError('Failed to load organization members');
    } finally {
      setIsLoading(false);
    }
  }, [user?.agency?.id, showError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Filtered members (memoized) ──

  const filteredMembers = useMemo(() => {
    if (!searchQuery) return members;
    const q = searchQuery.toLowerCase();
    return members.filter(
      m =>
        (m.first_name ?? '').toLowerCase().includes(q) ||
        (m.last_name ?? '').toLowerCase().includes(q) ||
        m.email.toLowerCase().includes(q)
    );
  }, [members, searchQuery]);

  // ── Summary stats (memoized) ──

  const stats = useMemo(
    () => ({
      totalMembers: members.length,
      withClinicAccess: members.filter(
        m => (m.clinic_assignments ?? []).length > 0
      ).length,
      totalClinics: clinics.length,
    }),
    [members, clinics]
  );

  // ── Edit dialog actions ──

  const openEditDialog = useCallback(
    (member: OrgMemberResponse) => {
      setEditingMember(member);

      const existingByClinic = new Map(
        (member.clinic_assignments ?? []).map(ca => [ca.clinic_id, ca])
      );

      const rows: ClinicAssignmentRow[] = clinics.map(c => {
        const existing = existingByClinic.get(c.id);
        return {
          clinic_id: c.id,
          clinic_name: c.name,
          assigned: !!existing,
          is_assignable_clinician: existing?.is_assignable_clinician ?? false,
          is_case_manager: existing?.is_case_manager ?? false,
          is_approver: existing?.is_approver ?? false,
          is_physician: existing?.is_physician ?? false,
        };
      });

      setAssignments(rows);
    },
    [clinics]
  );

  const closeEditDialog = useCallback(() => {
    setEditingMember(null);
    setAssignments([]);
  }, []);

  const toggleAssigned = useCallback((clinicId: string) => {
    setAssignments(prev =>
      prev.map(r =>
        r.clinic_id === clinicId
          ? {
              ...r,
              assigned: !r.assigned,
              // Clear flags when un-assigning
              ...(!r.assigned
                ? {}
                : {
                    is_assignable_clinician: false,
                    is_case_manager: false,
                    is_approver: false,
                    is_physician: false,
                  }),
            }
          : r
      )
    );
  }, []);

  const toggleFlag = useCallback((clinicId: string, flag: RoleFlag) => {
    setAssignments(prev =>
      prev.map(r => (r.clinic_id === clinicId ? { ...r, [flag]: !r[flag] } : r))
    );
  }, []);

  const toggleFlagForAllAssigned = useCallback(
    (flag: RoleFlag, value: boolean) => {
      setAssignments(prev =>
        prev.map(r => (r.assigned ? { ...r, [flag]: value } : r))
      );
    },
    []
  );

  const selectAll = useCallback(() => {
    setAssignments(prev => prev.map(r => ({ ...r, assigned: true })));
  }, []);

  const deselectAll = useCallback(() => {
    setAssignments(prev =>
      prev.map(r => ({
        ...r,
        assigned: false,
        is_assignable_clinician: false,
        is_case_manager: false,
        is_approver: false,
        is_physician: false,
      }))
    );
  }, []);

  const handleSave = useCallback(async () => {
    if (!editingMember?.roger_user_id) {
      showError('Unable to save: user record not found.');
      return;
    }

    setIsSaving(true);
    try {
      const body: ClinicAssignmentUpdate[] = assignments
        .filter(r => r.assigned)
        .map(r => ({
          clinic_id: r.clinic_id,
          is_assignable_clinician: r.is_assignable_clinician,
          is_case_manager: r.is_case_manager,
          is_approver: r.is_approver,
          is_physician: r.is_physician,
        }));

      await syncClinicAssignments({
        client,
        path: { user_id: editingMember.roger_user_id },
        body: { assignments: body },
      });

      showSuccess('Clinic assignments updated successfully');
      closeEditDialog();
      await loadData();
    } catch (err) {
      console.error('Failed to save clinic assignments:', err);
      showError('Failed to save clinic assignments');
    } finally {
      setIsSaving(false);
    }
  }, [
    editingMember,
    assignments,
    showSuccess,
    showError,
    closeEditDialog,
    loadData,
  ]);

  // ── Assign-all actions ──

  const openAssignAllDialog = useCallback(() => {
    setAssignAllDefaults({
      is_assignable_clinician: false,
      is_case_manager: false,
      is_approver: false,
      is_physician: false,
    });
    setShowAssignAllDialog(true);
  }, []);

  const closeAssignAllDialog = useCallback(() => {
    setShowAssignAllDialog(false);
  }, []);

  const handleAssignAll = useCallback(async () => {
    if (!editingMember?.roger_user_id) {
      showError(
        'This user has not logged in yet. They must log in at least once before clinic assignments can be managed.'
      );
      return;
    }

    setIsAssigningAll(true);
    try {
      await assignUserToAllClinics({
        client,
        path: { user_id: editingMember.roger_user_id },
        body: assignAllDefaults,
      });

      showSuccess('User assigned to all clinics successfully');
      setShowAssignAllDialog(false);
      closeEditDialog();
      await loadData();
    } catch (err) {
      console.error('Failed to assign to all clinics:', err);
      showError('Failed to assign user to all clinics');
    } finally {
      setIsAssigningAll(false);
    }
  }, [
    editingMember,
    assignAllDefaults,
    showSuccess,
    showError,
    closeEditDialog,
    loadData,
  ]);

  // ── NPI save handler ──

  const handleSaveNpi = useCallback(
    async (userId: string, npi: string) => {
      const trimmed = npi.trim();

      setSavingNpiForUserId(userId);
      try {
        await updateUserNpi({
          client,
          path: { user_id: userId },
          body: { npi: trimmed || null },
          throwOnError: true,
        });
        showSuccess('NPI updated');
        setMembers(prev =>
          prev.map(m =>
            m.roger_user_id === userId ? { ...m, npi: trimmed || null } : m
          )
        );
        return true;
      } catch (err: unknown) {
        console.error('Failed to update NPI:', err);
        const httpStatus = getHttpStatus(err);
        if (httpStatus === 409) {
          showError('This NPI is already associated with another user');
        } else {
          showError('Failed to update NPI');
        }
        return false;
      } finally {
        setSavingNpiForUserId(null);
      }
    },
    [showSuccess, showError]
  );

  // ── Permission check ──

  const hasAccess = hasAnyPermission(['clinician-mgr:access', 'emr:manage']);

  return {
    // Permission
    hasAccess,

    // Data
    members,
    clinics,
    filteredMembers,
    stats,
    isLoading,
    searchQuery,
    setSearchQuery,
    loadData,

    // Edit dialog
    editingMember,
    assignments,
    isSaving,
    openEditDialog,
    closeEditDialog,
    toggleAssigned,
    toggleFlag,
    toggleFlagForAllAssigned,
    selectAll,
    deselectAll,
    handleSave,

    // Assign-all dialog
    showAssignAllDialog,
    assignAllDefaults,
    setAssignAllDefaults,
    isAssigningAll,
    openAssignAllDialog,
    closeAssignAllDialog,
    handleAssignAll,

    // NPI editing
    handleSaveNpi,
    savingNpiForUserId,
  };
}
